import { useEffect, useRef, useState } from 'react';

const AnimatedCounter = ({ target, label, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const currentElement = counterRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Extract numeric value from target (e.g., "100+" -> 100, "150K+" -> 150000, "99%" -> 99)
    let numericValue = parseInt(target.replace(/[^0-9]/g, ''), 10);
    if (target.includes('K')) {
      numericValue *= 1000;
    }

    let startTime = null;

    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuad = 1 - Math.pow(1 - progress, 2);
      const currentValue = Math.floor(easeOutQuad * numericValue);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(numericValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  const formatNumber = (num) => {
    if (target.includes('K')) {
      return (num / 1000).toFixed(0) + 'K+';
    } else if (target.includes('%')) {
      return num + '%';
    } else {
      return num + '+';
    }
  };

  return (
    <div ref={counterRef}>
      <h3>{formatNumber(count)}</h3>
      <p>{label}</p>
    </div>
  );
};

export default AnimatedCounter;
